<template>
  <div>
    <h4 class="mb-3 mb-4 pb-3 border-bottom d-flex">
      <span>{{ title }}</span>

      <d-button
        v-if="!readOnly && (productsData.length || servicesData.length) && isEditMode && !currencyChanged"
        @click.prevent="updatePricesUsingCurrentPricing"
        theme="warning"
        :outline="true"
        :disabled="priceUpdateInProgress"
        class="mr-auto ml-5">
        <template v-if="priceUpdateInProgress">
          <div
            class="spinner-border spinner-border-sm"
            role="status"></div>
        </template>
        <template v-else>
          <i class="material-icons">sync</i>
          {{ $t('order.updatePricesUsingCurrentPricing') }}
        </template>
      </d-button>

      <d-button
        v-if="!readOnly"
        @click.prevent="addGlobalService"
        theme="primary"
        class="ml-auto">
        <i class="material-icons">add</i>
        {{ $t('service.addService') }}
      </d-button>

      <d-button
        v-if="!readOnly && allowServicePackagesAdd"
        @click.prevent="showServicesPackagePopup(null)"
        theme="warning"
        class="ml-2">
        <i class="material-icons">add</i>
        {{ $t('service.addServicesPackage') }}
      </d-button>

      <d-button
        v-if="!readOnly"
        @click.prevent="addProduct"
        theme="success"
        class="ml-2">
        <i class="material-icons">add</i>
        {{ $t('product.addProduct') }}
      </d-button>

      <d-button
        v-if="!readOnly"
        @click.prevent="openMultiProductsSearchPopup"
        theme="success"
        outline
        class="ml-2">
        <i class="material-icons">add</i>
        {{ $t('product.addMultipleProducts') }}
      </d-button>

      <d-button
        v-if="!readOnly && (!isOrder || isInquiryMode) && productsData.length"
        @click.prevent="removeAllProducts"
        theme="danger"
        class="ml-2">
        <i class="material-icons">delete</i>
        {{ $t('product.removeAllProducts') }}
      </d-button>
    </h4>

    <d-alert
      v-if="loadError"
      show
      theme="danger">
      {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
    </d-alert>

    <order-creator-product
      v-for="(productData, index) of productsData"
      :read-only="readOnly"
      :product-data="productData"
      :product-index="index"
      :currency-id="currencyId"
      :selected-currency-symbol="selectedCurrencySymbol"
      :customer-products-discount="customerProductsDiscount"
      :customer-services-discount="customerServicesDiscount"
      :products="productsToUse"
      :productsMinQty="productsMinQty"
      :services="services"
      :globalServices="servicesData"
      :globalServicesPackages="servicesPackages"
      :status="status"
      :includeSetupPrice="includeSetupPrice"
      :orderSetUpInProgress="orderSetUpInProgress"
      :datesNotRequired="datesNotRequired"
      :key="'product-' + index"
      :ref="'product-item-' + index"
      :warnings="warnings"
      :collectAlert="collectAlert"
      :collectWarning="collectWarning"
      :contractors="contractors"
      :isInquiryMode="isInquiryMode"
      :isOrder="isOrder"
      :isIndent="isIndent"
      :isOffer="isOffer"
      :isVirtual="isVirtual"
      :canAddDestroyedItemsInfo="canAddDestroyedItemsInfo"
      :deadline="deadline"
      :orderPurchases="orderPurchases"
      :embroideryItems="embroideryItems"
      :availabilityRefreshInProgress="availabilityRefreshInProgress"
      :calculatedServicesList="calculatedServicesList"
      :servicesGroups="servicesGroups"
      :expandedServicePackages="expandedServicePackages" />

    <d-card
      v-if="servicesData.length"
      class="mb-4">
      <table class="table mb-0">
        <thead class="bg-success-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('service.serviceAndContractor') }}</th>
            <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.amount') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('price.pricePerItem') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('service.setupPrice') }}</th>
            <th scope="col" class="border-0" style="width: 180px;">{{ $t('order.totalCost') }}</th>
            <th scope="col" class="border-0" style="width: 80px;" v-if="!readOnly">{{ $t('ui.delete') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(serviceData, index) of servicesData">
            <order-creator-service-global-package
              v-if="firstServicesInPackage.indexOf(serviceData.service_id_in_order ? serviceData.service_id_in_order : serviceData.sid) !== -1"
              :key="'global-service-package-' + index"
              :ref="'gloabl-service-package-' + index"
              :servicePackageID="serviceData.service_package_id"
              :servicePackages="servicesPackages"
              :isIndent="isIndent"
              :isOffer="isOffer"
              :assignedQuantity="globalServicePackagesAssignedQuantities[serviceData.service_package_id]"
              :totals="globalServicePackagesTotals[serviceData.service_package_id]"
              :selectedCurrencySymbol="selectedCurrencySymbol"
              :isCollapsed="expandedServicePackages.indexOf(serviceData.service_package_id) === -1"
              @toggle-collapse="toggleServicePackageCollapse"
              :servicesGroups="servicesGroups"
              :packageServices="servicesData.filter(item => item.service_package_id === serviceData.service_package_id)"
              :orderSetUpInProgress="orderSetUpInProgress"
              :readOnly="readOnly" />
            <order-creator-service-global
              v-show="!serviceData.service_package_id || expandedServicePackages.indexOf(serviceData.service_package_id) !== -1"
              :key="'global-service-in-package-' + index"
              :ref="'global-service-in-package-' + index"
              :read-only="readOnly"
              :service-data="serviceData"
              :service-index="index"
              :currency-id="currencyId"
              :selected-currency-symbol="selectedCurrencySymbol"
              :customer-services-discount="customerServicesDiscount"
              :services="services"
              :quantity="totalProductItemsCountWithExcluded"
              :status="status"
              :includeSetupPrice="includeSetupPrice"
              :orderSetUpInProgress="orderSetUpInProgress"
              :datesNotRequired="datesNotRequired"
              :warnings="warnings"
              :contractors="contractors"
              :deadline="deadline"
              :embroideryItems="embroideryItems"
              :isIndent="isIndent"
              :assignedQuantities="globalServicesAssignedQuantities"
              :isFirstInPackage="firstServicesInPackage.indexOf(serviceData.service_id_in_order ? serviceData.service_id_in_order : serviceData.sid) !== -1"
              :isLastInPackage="lastServicesInPackage.indexOf(serviceData.service_id_in_order ? serviceData.service_id_in_order : serviceData.sid) !== -1"
              :calculatedServicesList="calculatedServicesList" />
          </template>
        </tbody>
      </table>
    </d-card>

    <d-row v-if="!readOnly && (productsData.length || servicesData.length)">
      <p class="text-center w-100">
        <d-button
          @click.prevent="addGlobalService"
          theme="primary">
          <i class="material-icons">add</i>
          {{ $t('service.addService') }}
        </d-button>

        <d-button
          v-if="allowServicePackagesAdd"
          @click.prevent="showServicesPackagePopup(null)"
          theme="warning"
          class="ml-2">
          <i class="material-icons">add</i>
          {{ $t('service.addServicesPackage') }}
        </d-button>

        <d-button
          @click.prevent="addProduct"
          theme="success"
          class="ml-2">
          <i class="material-icons">add</i>
          {{ $t('product.addProduct') }}
        </d-button>
      </p>
    </d-row>

    <p
      v-if="!productsData.length && !servicesData.length"
      class="text-center">
      {{ $t('order.noProductsOrServicesIn') }} {{ titleLabel }}&hellip;
    </p>

    <hr
      v-if="productsData.length || servicesData.length"
      class="mb-5 mt-5">

    <d-row v-if="productsData.length || servicesData.length">
      <h5
        v-if="totalOrderPrice.totalPrice"
        class="text-right mt-0 mb-3 ml-auto mr-3 w-100">
        {{ $t('order.orderCostNet') }}:
        <strong class="text-primary">
          {{ totalOrderPrice.totalDiscountedPrice }}
          {{ selectedCurrencySymbol }}
        </strong>

        <div v-if="totalOrderPrice.totalDiscountedPrice !== totalOrderPrice.totalPrice">
          <small>
            {{ $t('price.priceBeforeDicount') }}:
            <del class="text-danger">
              {{ totalOrderPrice.totalPrice }} {{ selectedCurrencySymbol }}
            </del>
          </small>
        </div>

        <div
          v-if="shipperPrice"
          class="mt-4">
          <small>
            {{ $t('order.dispatchCost') }}:
            <span class="text-primary">
              {{ Number(shipperPrice).toFixed(2) }} {{ selectedCurrencySymbol }}
            </span>
          </small>
        </div>

        <div v-if="shipperAdditionalPrice">
          <small>
            {{ $t('order.additionalDispatchCost') }}:
            <span class="text-primary">
              {{ Number(shipperAdditionalPrice).toFixed(2) }} {{ selectedCurrencySymbol }}
            </span>
          </small>
        </div>
      </h5>

      <small class="text-right w-100 mb-0 mt-0 mr-3">
        {{ $t('order.numberOfOrderdProducts') }}: <strong>{{ productsData.length }}</strong>
      </small>

      <small
        v-if="totalServicesCount && !isNaN(totalServicesCount)"
        class="text-right w-100 mb-0 mt-0 mr-3">
        {{ $t('order.numberOfOrderdServices') }}: <strong>{{ totalServicesCount }}</strong>
      </small>

      <small
        v-if="!isNaN(totalProductItemsCount)"
        class="text-right w-100 mb-3 mt-0 mr-3">
        {{ $t('order.numberOfOrderdItems') }}: <strong>{{ totalProductItemsCount }}</strong>
      </small>

      <h5
        v-if="skonto > 0"
        class="w-100 text-right mr-3">
        Skonto:
        <strong class="text-success">
          {{ roundProperly(Number(skonto)) }} {{ selectedCurrencySymbol }}
        </strong>
      </h5>

      <h5
        v-if="!isNaN(shipperPrice) && !isNaN(totalOrderPrice.totalDiscountedPrice)"
        class="w-100 text-right mr-3">
        {{ $t('order.totalNet') }}:
        <strong
          v-if="skonto > 0"
          class="text-success">
          {{ roundProperly(Number(shipperPrice) + Number(shipperAdditionalPrice) + Number(totalOrderPrice.totalDiscountedPrice) - Number(skonto)) }} {{ selectedCurrencySymbol }}
        </strong>
        <strong
          v-else
          class="text-success">
          {{ roundProperly(Number(shipperPrice) + Number(shipperAdditionalPrice) + Number(totalOrderPrice.totalDiscountedPrice)) }} {{ selectedCurrencySymbol }}
        </strong>
      </h5>
    </d-row>

    <d-modal
      v-if="displaySamplePreviewPopup"
      @close="hideSamplePreviewPopup"
      size="lg">
      <d-modal-body class="p-0">
        <d-alert
          :show="!samplePreviewLoaded"
          theme="light"
          class="mt-3 text-center">
          {{ $t('ui.dataLoading') }}
        </d-alert>
        <img
          v-show="samplePreviewLoaded"
          :src="samplePreviewURL"
          @load="samplePreviewLoaded = true;"
          style="width: 100%; height: auto;" />
      </d-modal-body>
      <d-modal-footer>
        <d-button
          class="ml-auto mr-2"
          theme="primary"
          @click.prevent="openInNewTab(samplePreviewURL)">
          {{ $t('ui.viewInNewTab') }}
        </d-button>

        <d-button
          class="mr-auto"
          theme="danger"
          @click.prevent="hideSamplePreviewPopup">
          {{ $t('ui.close') }}
        </d-button>
      </d-modal-footer>
    </d-modal>

    <d-modal
      v-if="displayServicesPackagePopup"
      @close="hideServicesPackagePopup"
      size="lg">
      <d-modal-header>
        <d-modal-title>
          {{ $t('service.servicesPackagePopup.title') }}
        </d-modal-title>
      </d-modal-header>

      <d-modal-body class="p-5">
        <validation-observer
          ref="select-services-package-form"
          tag="form"
          @keydown.enter.prevent>
          <div class="form-group">
            <label>
              {{ $t('service.servicesPackagePopup.selectServicesPackage') }}:
            </label>

            <validation-provider
              rules="required"
              name="service-package"
              v-slot="{ errors }">
              <div :class="{
                'form-control': true,
                'is-select': true,
                'is-invalid': errors.length
              }">
                <v-select
                  :options="packageGroups"
                  v-model="selectedServicesPackage"
                  :reduce="pkg => pkg.id"
                  :filterBy="filterByForServicesPackages"
                  :placeholder="$t('service.servicesPackagePopup.selectServicesPackage')"
                  label="name">
                  <template slot="no-options">
                    {{ $t('service.servicesPackagePopup.noServicesPackagesMatchingGivenPhrase') }}&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center w-100 normal-white-space">
                      {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center w-100 normal-white-space">
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div
            v-if="selectedServicesPackage"
            class="mt-3">
            <small>
              <strong>{{ $t('service.servicesPackagePopup.includedServices') }}</strong>
            </small>

            <ul
              v-if="contentOfSelectedServicesPackage.length"
              class="mb-0">
              <li
                v-for="(packageService, index) of contentOfSelectedServicesPackage"
                :key="'package-service-to-add-' + index">
                <small>{{ packageService.name }}</small>
              </li>
            </ul>
            <div v-else>
              <small>{{ $t('service.servicesPackagePopup.noServicesIncluded') }}</small>
            </div>
          </div>
        </validation-observer>
      </d-modal-body>

      <d-modal-footer>
        <d-button
          class="mr-auto"
          theme="danger"
          @click.prevent="hideServicesPackagePopup">
          {{ $t('ui.cancel') }}
        </d-button>

        <d-button
          class="ml-auto mr-2"
          theme="success"
          @click.prevent="validateServicesPackageForm">
          {{ $t('service.servicesPackagePopup.addServicesPackage') }}
        </d-button>
      </d-modal-footer>
    </d-modal>

    <products-search-popup
      v-if="!readOnly && productsSearchPopupIsOpen"
      :product-index="productsSearchPopupProductIndex"
      :currency-id="currencyId"
      :isIndent="isIndent"
      :products-discount="customerProductsDiscount"
      :selected-currency-symbol="selectedCurrencySymbol"
      :tag-categories="tagCategories"
      :orderCreatorMode="true"
      :multi-select-mode="productsSearchPopupMultiMode"
      :saveFilters="true" />

    <order-box-products-comments-popup
      v-if="displayBoxProductsCommentsPopup"
      :dataToDisplay="boxProductsComments" />
  </div>
</template>

<script>
import AppConfig from './../../.env';
import CustomDecimals from './../../utils/CustomDecimals.js';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import OrderBoxProductsCommentsPopup from './OrderBoxProductsCommentsPopup.vue';
import OrderCreatorProduct from './OrderCreatorProduct.vue';
import OrderCreatorServiceGlobal from './OrderCreatorServiceGlobal.vue';
import OrderCreatorServiceGlobalPackage from './OrderCreatorServiceGlobalPackage';
import ProductsSearchPopup from './ProductsSearchPopup.vue';
import Vue from 'vue';

export default {
  name: 'order-creator',
  mixins: [
    CustomerPanel
  ],
  props: [
    'title',
    'titleLabel',
    'status',
    'currencyId',
    'initialCurrencyId',
    'products',
    'indentProducts',
    'services',
    'selectedCurrencySymbol',
    'shipperPrice',
    'shipperAdditionalPrice',
    'includeSetupPrice',
    'orderSetUpInProgress',
    'customerProductsDiscount',
    'customerServicesDiscount',
    'readOnly',
    'isEditMode',
    'isIndent',
    'isInquiryMode',
    'isOrder',
    'isVirtual',
    'warnings',
    'datesNotRequired',
    'collectAlert',
    'collectWarning',
    'contractors',
    'canAddDestroyedItemsInfo',
    'tagCategories',
    'deadline',
    'orderPurchases',
    'embroideryItems',
    'isOffer',
    'skonto',
    'servicesGroups',
    'allPackagesData'
  ],
  components: {
    'order-box-products-comments-popup': OrderBoxProductsCommentsPopup,
    'order-creator-product': OrderCreatorProduct,
    'order-creator-service-global': OrderCreatorServiceGlobal,
    'order-creator-service-global-package': OrderCreatorServiceGlobalPackage,
    'products-search-popup': ProductsSearchPopup
  },
  computed: {
    currencyChanged () {
      if (!this.initialCurrencyId || !this.currencyId) {
        return false;
      }

      return parseInt(this.currencyId, 10) !== parseInt(this.initialCurrencyId, 10);
    },
    allowServicePackagesAdd () {
      return !!AppConfig.additionalConfig[AppConfig.useConfig].allowServicePackagesAdd;
    },
    productsToUse () {
      if (this.isIndent && this.isOffer) {
        return this.products.concat(this.indentProducts);
      }

      return this.products;
    },
    productsMinQty () {
      let output = {};

      for (let i = 0; i < this.productsToUse.length; i++) {
        let product = this.productsToUse[i];
        output[product.id] = product.minimum_quantity;
      }

      return output;
    },
    totalProductItemsCount () {
      return this.productsData.reduce((prev, product) => prev + parseInt(product.count, 10), 0);
    },
    totalProductItemsCountWithExcluded () {
      return this.productsData.reduce((prev, product) => prev + (product.include_in_services ? parseInt(product.count, 10) : 0), 0);
    },
    totalServicesCount () {
      return this.productsData.reduce((prev, product) => prev + parseInt(product.services.length, 10), 0) + this.servicesData.length;
    },
    orderProductIDs () {
      return this.productsData.reduce((prev, product) => prev + ';' + parseInt(product.id, 10), '');
    },
    productTotalPrices () {
      return this.customerProductsDiscount + '|' + this.productsData.map(product => ([product.price, product.count, product.use_custom_price, product.for_free].join(';'))).join('|');
    },
    productsIdAndQtyChanges () {
      if (!this.productsData) {
        return '';
      }

      return this.productsData.map(product => product.id + ';' + product.count).join('|');
    },
    servicesTotalPrices () {
      return this.customerServicesDiscount + '|' + this.includeSetupPrice + '|' + this.productsData.map(product => product.count + ',' + product.services.map(service => service.price).join(';') + ',' + product.services.map(service => service.setupPrice).join(';') + ',' + product.services.map(service => service.count + ',' + service.use_custom_price).join(';')).join('|');
    },
    globalServicesTotalPrices () {
      let globalServicesPart = this.servicesData.map(service => [service.price, service.setupPrice, service.count, service.use_custom_price].join(',')).join(';');
      return this.customerServicesDiscount + '|' + this.includeSetupPrice + '|' + globalServicesPart + '|' + this.productsData.map(product => product.count).join(';');
    },
    totalOrderPrice () {
      let totalPrice = this.productsData
        .filter(product => !Number.isNaN(parseFloat(product.totalPrice)))
        .map(product => parseFloat(product.totalPrice))
        .reduce((prev, price) => prev + parseFloat(price), 0);
      let productsDiscountedPrice = this.productsData
        .filter(product => !Number.isNaN(parseFloat(product.totalDiscountedPrice)))
        .map(product => parseFloat(product.totalDiscountedPrice))
        .reduce((prev, price) => prev + parseFloat(price), 0);
      let servicesTotalPrice = 0;
      let servicesDiscountedPrice = 0;
      let globalServicesTotalPrice = 0;
      let globalServicesDiscountedPrice = 0;

      if (Number.isNaN(totalPrice)) {
        return '';
      }

      if (!(this.isIndent && this.isOffer)) {
        for (let i = 0; i < this.productsData.length; i++) {
          for (let j = 0; j < this.productsData[i].services.length; j++) {
            if (!Number.isNaN(parseFloat(this.productsData[i].services[j].totalPrice))) {
              servicesTotalPrice += parseFloat(this.productsData[i].services[j].totalPrice);
            }
          }
        }

        for (let i = 0; i < this.productsData.length; i++) {
          for (let j = 0; j < this.productsData[i].services.length; j++) {
            if (!Number.isNaN(parseFloat(this.productsData[i].services[j].totalDiscountedPrice))) {
              servicesDiscountedPrice += parseFloat(this.productsData[i].services[j].totalDiscountedPrice);
            }
          }
        }
      }

      totalPrice += servicesTotalPrice;

      for (let i = 0; i < this.servicesData.length; i++) {
        if (!Number.isNaN(parseFloat(this.servicesData[i].totalPrice))) {
          globalServicesTotalPrice += parseFloat(this.servicesData[i].totalPrice);
        }
      }

      for (let i = 0; i < this.servicesData.length; i++) {
        if (!Number.isNaN(parseFloat(this.servicesData[i].totalDiscountedPrice))) {
          globalServicesDiscountedPrice += parseFloat(this.servicesData[i].totalDiscountedPrice);
        }
      }

      totalPrice += globalServicesTotalPrice;

      if (this.customerProductsDiscount > 0 || this.customerServicesDiscount > 0) {
        return {
          totalPrice: CustomDecimals.roundProperly(totalPrice),
          totalDiscountedPrice: CustomDecimals.roundProperly(productsDiscountedPrice + servicesDiscountedPrice + globalServicesDiscountedPrice)
        };
      }

      return {
        totalPrice: CustomDecimals.roundProperly(totalPrice),
        totalDiscountedPrice: CustomDecimals.roundProperly(totalPrice)
      };
    },
    firstServicesInPackage () {
      let foundedPackageIds = [];
      let ids = [];

      for (let i = 0; i < this.servicesData.length; i++) {
        let service = this.servicesData[i];

        if (service.service_package_id && foundedPackageIds.indexOf(service.service_package_id) === -1) {
          foundedPackageIds.push(service.service_package_id);
          ids.push(service.service_id_in_order ? service.service_id_in_order : service.sid);
        }
      }

      return ids;
    },
    lastServicesInPackage () {
      let ids = {};
      let output = [];

      for (let i = 0; i < this.servicesData.length; i++) {
        let service = this.servicesData[i];

        if (service.service_package_id) {
          ids[service.service_package_id] = service.service_id_in_order ? service.service_id_in_order : service.sid;
        }
      }

      let packageIDs = Object.keys(ids);

      for (let i = 0; i < packageIDs.length; i++) {
        output.push(ids[packageIDs[i]]);
      }

      return output;
    },
    packageGroups () {
      let servicesGroups = this.servicesGroups || [];
      return servicesGroups.filter(group => group.is_package);
    },
    contentOfSelectedServicesPackage () {
      if (!this.selectedServicesPackage) {
        return [];
      }

      let selectedGroup = this.packageGroups.filter(group => group.id === this.selectedServicesPackage);

      if (selectedGroup && selectedGroup[0] && selectedGroup[0].package_services) {
        return selectedGroup[0].package_services;
      }

      return [];
    },
    globalServicesAssignedQuantities () {
      let output = {};

      for (let i = 0; i < this.productsData.length; i++) {
        let assignments = this.productsData[i].global_services;

        if (!assignments) {
          // eslint-disable-next-line
          continue;
        }

        let assignmentsKeys = Object.keys(assignments);

        for (let j = 0; j < assignmentsKeys.length; j++) {
          let quantity = parseInt(assignments[assignmentsKeys[j]], 10);

          if (Number.isNaN(quantity)) {
            // eslint-disable-next-line
            continue;
          }

          output[assignmentsKeys[j]] = (output[assignmentsKeys[j]] ? output[assignmentsKeys[j]] : 0) + quantity;
        }
      }

      return output;
    },
    globalServicePackagesAssignedQuantities () {
      let output = {};

      for (let i = 0; i < this.servicesPackages.length; i++) {
        let pkg = this.servicesPackages[i];

        if (!pkg.id) {
          // eslint-disable-next-line
          continue;
        }

        let packageServicesKeys = this.servicesData
          .filter(service => service.service_package_id === pkg.id)
          .map(service => service.service_id_in_order ? service.service_id_in_order : service.sid);
        let total = 0;

        for (let j = 0; j < this.productsData.length; j++) {
          let assignments = this.productsData[j].global_services;

          if (!assignments) {
            // eslint-disable-next-line
            continue;
          }

          for (let k = 0; k < packageServicesKeys.length; k++) {
            let quantity = parseInt(assignments[packageServicesKeys[k]], 10);

            if (!Number.isNaN(quantity)) {
              total += quantity;
              break;
            }
          }
        }

        output[pkg.id] = total;
      }

      return output;
    },
    globalServicePackagesTotals () {
      let output = {};

      for (let i = 0; i < this.servicesData.length; i++) {
        let service = this.servicesData[i];

        if (!service.service_package_id) {
          // eslint-disable-next-line
          continue;
        }

        if (!output[service.service_package_id]) {
          output[service.service_package_id] = {
            price: 0,
            setupPrice: 0,
            totalPrice: 0,
            totalDiscountedPrice: 0
          };
        }

        let packageTotals = output[service.service_package_id];
        let price = parseFloat(service.price);
        let setupPrice = parseFloat(service.setupPrice);
        let totalPrice = parseFloat(service.totalPrice);
        let totalDiscountedPrice = parseFloat(service.totalDiscountedPrice);

        if (!Number.isNaN(price)) {
          packageTotals.price += price;
        }

        if (!Number.isNaN(setupPrice)) {
          packageTotals.setupPrice += setupPrice;
        }

        if (!Number.isNaN(totalPrice)) {
          packageTotals.totalPrice += totalPrice;
        }

        if (!Number.isNaN(totalDiscountedPrice)) {
          packageTotals.totalDiscountedPrice += totalDiscountedPrice;
        } else if (!Number.isNaN(totalPrice)) {
          packageTotals.totalDiscountedPrice += totalPrice;
        }
      }

      let packageIDs = Object.keys(output);

      for (let i = 0; i < packageIDs.length; i++) {
        let packageTotals = output[packageIDs[i]];

        output[packageIDs[i]] = {
          price: CustomDecimals.roundProperly(packageTotals.price),
          setupPrice: CustomDecimals.roundProperly(packageTotals.setupPrice),
          totalPrice: CustomDecimals.roundProperly(packageTotals.totalPrice),
          totalDiscountedPrice: CustomDecimals.roundProperly(packageTotals.totalDiscountedPrice)
        };
      }

      return output;
    },
    calculatedServicesList () {
      let output = {};
      let packagesData = (this.allPackagesData || []).slice();
      let servicesGroups = this.servicesGroups || [];
      let localPackages = this.servicesPackages || [];

      for (let i = 0; i < localPackages.length; i++) {
        packagesData.push(localPackages[i]);
      }

      for (let i = 0; i < this.productsData.length; i++) {
        let productPackages = this.productsData[i].services_packages || [];

        for (let j = 0; j < productPackages.length; j++) {
          packagesData.push(productPackages[j]);
        }
      }

      for (let i = 0; i < packagesData.length; i++) {
        let pkg = packagesData[i];
        let servicesGroup = servicesGroups.find(group => group.id === pkg.group_id);

        if (!servicesGroup || !servicesGroup.calculator_services) {
          output[pkg.id] = [];
          // eslint-disable-next-line
          continue;
        }

        output[pkg.id] = servicesGroup.calculator_services.map(service => service.id ? service.id : service);
      }

      return output;
    }
  },
  watch: {
    productsIdAndQtyChanges () {
      if (this.orderSetUpInProgress) {
        return;
      }

      this.$bus.$emit('order-puesc-product-id-or-qty-change');
    },
    productTotalPrices () {
      for (let i = 0; i < this.productsData.length; i++) {
        if (!Number.isNaN(parseFloat(this.productsData[i].price))) {
          this.productsData[i].totalPrice = CustomDecimals.roundProperly(this.productsData[i].count * parseFloat(this.productsData[i].price));
          let itemDiscountedPrice = parseFloat(this.productsData[i].price);

          if (this.customerProductsDiscount > 0 && this.productsData[i].include_discounts && !this.productsData[i].use_custom_price && !this.productsData[i].for_free) {
            itemDiscountedPrice = parseFloat(CustomDecimals.roundProperly(parseFloat(this.productsData[i].price) * ((100 - this.customerProductsDiscount) / 100)));
          }

          Vue.set(this.productsData[i], 'totalDiscountedPrice', CustomDecimals.roundProperly(this.productsData[i].count * itemDiscountedPrice));
        }
      }
    },
    servicesTotalPrices () {
      if (this.isIndent && this.isOffer) {
        return;
      }

      Vue.nextTick(() => {
        for (let i = 0; i < this.productsData.length; i++) {
          let services = this.productsData[i].services;

          for (let j = 0; j < services.length; j++) {
            let servicesDiscount = this.customerServicesDiscount;
            let setupPrice = parseFloat(this.productsData[i].services[j].setupPrice);
            let quantity = parseInt(this.productsData[i].services[j].count, 10);
            let itemPrice = parseFloat(this.productsData[i].services[j].price);
            let serviceMultipleFrom = parseInt(this.productsData[i].services[j].serviceData.price_multiple_from, 10);

            if (this.productsData[i].services[j].use_custom_price) {
              servicesDiscount = 0;
            }

            if (!Number.isNaN(itemPrice) && !Number.isNaN(setupPrice)) {
              let totalPrice = parseFloat(itemPrice) + parseFloat(setupPrice);
              let itemDiscountedPrice = parseFloat(CustomDecimals.roundProperly(parseFloat(itemPrice) * ((100 - servicesDiscount) / 100)));
              let totalDiscountedPrice = parseFloat(itemDiscountedPrice) + parseFloat(setupPrice * ((100 - servicesDiscount) / 100));

              if (quantity >= serviceMultipleFrom) {
                totalPrice = parseFloat(quantity * itemPrice) + parseFloat(Number(setupPrice).toFixed(2));
                totalDiscountedPrice = parseFloat(quantity * itemDiscountedPrice) + parseFloat(CustomDecimals.roundProperly(setupPrice * ((100 - servicesDiscount) / 100)));
              }

              Vue.set(this.productsData[i].services[j], 'totalPrice', CustomDecimals.roundProperly(totalPrice));
              Vue.set(this.productsData[i].services[j], 'totalDiscountedPrice', CustomDecimals.roundProperly(totalDiscountedPrice));
            }
          }
        }
      });
    },
    globalServicesTotalPrices () {
      Vue.nextTick(() => {
        for (let i = 0; i < this.servicesData.length; i++) {
          let servicesDiscount = this.customerServicesDiscount;
          let setupPrice = parseFloat(this.servicesData[i].setupPrice);
          let quantity = parseInt(this.servicesData[i].count, 10);
          let itemPrice = parseFloat(this.servicesData[i].price);
          let serviceMultipleFrom = parseInt(this.servicesData[i].serviceData.price_multiple_from, 10);

          if (this.servicesData[i].use_custom_price) {
            servicesDiscount = 0;
          }

          if (!Number.isNaN(itemPrice) && !Number.isNaN(setupPrice)) {
            let totalPrice = parseFloat(itemPrice) + parseFloat(setupPrice);
            let itemDiscountedPrice = parseFloat(CustomDecimals.roundProperly(parseFloat(itemPrice) * ((100 - servicesDiscount) / 100)));
            let totalDiscountedPrice = parseFloat(itemDiscountedPrice) + parseFloat(CustomDecimals.roundProperly(setupPrice * ((100 - servicesDiscount) / 100)));

            if (quantity >= serviceMultipleFrom) {
              totalPrice = parseFloat(quantity * itemPrice) + parseFloat(Number(setupPrice).toFixed(2));
              totalDiscountedPrice = parseFloat(quantity * itemDiscountedPrice) + parseFloat(CustomDecimals.roundProperly(setupPrice * ((100 - servicesDiscount) / 100)));
            }

            Vue.set(this.servicesData[i], 'totalPrice', CustomDecimals.roundProperly(totalPrice));
            Vue.set(this.servicesData[i], 'totalDiscountedPrice', CustomDecimals.roundProperly(totalDiscountedPrice));
          }
        }
      });
    },
    totalProductItemsCount (newValue, oldValue) {
      if (
        (oldValue > 0 && newValue === 0) ||
        (oldValue === 0 && newValue > 0) ||
        (oldValue <= 25 && newValue > 25) ||
        (oldValue > 25 && newValue <= 25)
      ) {
        this.$bus.$emit('order-check-shipping-price', 'products-qty-change');
      }

      this.$bus.$emit('order-updated-products-qty', this.cartonsAmount());
    },
    orderProductIDs () {
      this.$bus.$emit('order-updated-products-ids', this.cartonsAmount());
      this.refreshProductsAvailability();
    }
  },
  data () {
    return {
      productsSearchPopupIsOpen: false,
      productsSearchPopupProductIndex: 0,
      productsSearchPopupMultiMode: false,
      productsData: [],
      servicesData: [],
      servicesPackages: [],
      expandedServicePackages: [],
      loadedService: false,
      loadedProducts: false,
      loadError: false,
      priceUpdateInProgress: false,
      availabilityRefreshInProgress: false,
      displaySamplePreviewPopup: false,
      samplePreviewURL: '',
      samplePreviewLoaded: false,
      displayBoxProductsCommentsPopup: false,
      boxProductsComments: null,
      displayServicesPackagePopup: false,
      selectedServicesPackage: null,
      assignServicesPackageTo: null
    }
  },
  created () {
    this.debouncedRefreshProductsAvailability = debounce((productIDs) => {
      FormUtils.submitInBackground({
        $http: this.$http,
        method: 'get',
        endpoint: this.getApiEndpoint('/products/availability'),
        formData: {
          params: {
            ids: productIDs
          }
        },
        skipErrorDisplay: true,
        successAction: (data) => {
          let items = data.data;

          for (let i = 0; i < items.length; i++) {
            let productID = items[i].id;
            let availability = items[i].availability;

            for (let j = 0; j < this.productsData.length; j++) {
              if (parseInt(this.productsData[j].id, 10) === productID) {
                Vue.set(this.productsData[j], 'availability', availability);
              }
            }

            let foundedProduct = this.products.filter(product => product.id === productID);

            if (foundedProduct.length) {
              Vue.set(foundedProduct[0], 'availability', availability);
            }
          }

          this.availabilityRefreshInProgress = false;
        },
        failAction: () => {
          this.availabilityRefreshInProgress = false;
        }
      });
    }, 1000);
  },
  mounted () {
    this.$bus.$on('products-search-popup-select-product', this.setProduct);
    this.$bus.$on('products-search-popup-select-multiple-products', this.setMultipleProducts);
    this.$bus.$on('set-new-indent-product-in-order', this.setIndentProduct);
    this.$bus.$on('order-creator-move-product-from-inquiry', this.moveProductFromInquiry);
    this.$bus.$on('order-status-check-availability', this.checkAvailability);
    this.$bus.$on('products-search-popup-open', this.openProductsSearchPopup);
    this.$bus.$on('products-search-popup-close', this.closeProductsSearchPopup);
    this.$bus.$on('order-show-sample-preview', this.showSamplePreviewPopup);
    this.$bus.$on('order-creator-box-products-comments-popup-open', this.showBoxProductsCommentsPopup);
    this.$bus.$on('order-creator-box-products-comments-popup-close', this.closeBoxProductsCommentsPopup);
    this.$bus.$on('order-reset-custom-prices', this.resetCustomPrices);
    this.$bus.$on('order-remove-service-package-from-product', this.removeServicePackageFromProduct);
    this.$bus.$on('order-update-service-package-qty-in-product', this.updateServicePackageQtyInProduct);
    this.$bus.$on('order-update-global-service-package-qty', this.updateGlobalServicePackageQty);
    this.$bus.$on('order-remove-global-service-package', this.removeGlobalServicePackage);
    this.$bus.$on('order-creator-add-services-package', this.showServicesPackagePopup);
    this.$bus.$on('order-package-calculator-result', this.applyCalculatorResult);
  },
  methods: {
    buildEmptyProductData () {
      return {
        id: '',
        productData: {},
        count: 1,
        price: '',
        totalPrice: '',
        totalDiscountedPrice: '',
        services: [],
        global_services: {},
        description: '',
        description_for_customer: '',
        destroyed: 0,
        file: '',
        file_url: '',
        is_sale: 0,
        include_discounts: true,
        include_in_services: true,
        hasDescription: false,
        hasDescriptionForCustomer: false,
        hasDestroyedItems: false,
        hasVisualisation: false,
        containers: [],
        purchase_quantity: 0,
        purchase_product_id: 0,
        purchase_product: null,
        exclude_in_invoice: false,
        moq: true,
        for_free: false,
        use_custom_price: false,
        services_packages: []
      };
    },
    addProduct () {
      this.productsData.push(this.buildEmptyProductData());
    },
    setMultipleProducts (products) {
      if (!products || !products.length) {
        return;
      }

      let startIndex = this.productsData.length;

      for (let i = 0; i < products.length; i++) {
        let productData = this.buildEmptyProductData();
        productData.id = products[i].id;
        this.productsData.push(productData);
      }

      Vue.nextTick(() => {
        let pendingDefaultServices = [];

        for (let i = 0; i < products.length; i++) {
          let productIndex = startIndex + i;
          let productID = products[i].id;
          let productRef = this.$refs['product-item-' + productIndex];

          if (productRef && productRef[0]) {
            productRef[0].updateProductPrice(productID);
            productRef[0].updateProductSaleState(productID);

            // Don't prompt per-product here — sweetalert2 shows a single modal at a
            // time, so firing N confirmations would drop all but the last. Collect
            // the products with default services and ask once below.
            let defaultServiceName = productRef[0].getPendingDefaultServiceName(productID);

            if (defaultServiceName) {
              pendingDefaultServices.push({ ref: productRef[0], productID, name: defaultServiceName });
            }
          }

          if (products[i].count) {
            Vue.set(this.productsData[productIndex], 'count', products[i].count);
          }
        }

        if (pendingDefaultServices.length) {
          this.confirmDefaultServicesForProducts(pendingDefaultServices);
        }
      });
    },
    confirmDefaultServicesForProducts (pendingDefaultServices) {
      let productsList = pendingDefaultServices.map(item => item.name).join('<br>');

      Vue.swal({
        title: this.$t('product.defaultServicesConfirm.addForMultipleTitle'),
        html: this.$t('product.defaultServicesConfirm.addForMultipleText') + '<br><br>' + productsList,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('product.defaultServicesConfirm.yesPleaseAdd'),
        cancelButtonText: this.$t('ui.cancel'),
        buttonsStyling: true
      }).then((result) => {
        if (result.value) {
          for (let i = 0; i < pendingDefaultServices.length; i++) {
            pendingDefaultServices[i].ref.applyDefaultServices(pendingDefaultServices[i].productID);
          }
        }
      });
    },
    toggleServicePackageCollapse (packageID) {
      let packageIndex = this.expandedServicePackages.indexOf(packageID);

      if (packageIndex === -1) {
        this.expandedServicePackages.push(packageID);
        return;
      }

      this.expandedServicePackages.splice(packageIndex, 1);
    },
    addGlobalService () {
      let globalServiceID = +new Date();
      this.servicesData.push({
        sid: globalServiceID,
        id: '',
        serviceData: {},
        price: '',
        exclude_in_invoice: false,
        use_custom_price: false,
        samples: [],
        count: this.productsData.reduce((prev, product) => prev + (product.include_in_services ? parseInt(product.count, 10) : 0), 0),
        setupPrice: '',
        totalPrice: '',
        totalDiscountedPrice: '',
        embroidery_estimated_date: null,
        embroidery_date_tokens: [],
        service_package_id: null
      });

      for (let i = 0; i < this.productsData.length; i++) {
        Vue.set(this.productsData[i].global_services, globalServiceID, this.productsData[i].count);
      }
    },
    moveProductFromInquiry (productID, quantity) {
      this.productsData.push({
        id: productID,
        productData: {},
        count: 1,
        price: '',
        totalPrice: '',
        totalDiscountedPrice: '',
        services: [],
        description: '',
        description_for_customer: '',
        destroyed: 0,
        file: '',
        file_url: '',
        include_discounts: true,
        include_in_services: true,
        hasDescription: false,
        hasDescriptionForCustomer: false,
        hasDestroyedItems: false,
        hasVisualisation: false,
        containers: [],
        exclude_in_invoice: false,
        moq: true,
        for_free: false,
        use_custom_price: false
      });

      Vue.nextTick(() => {
        let productIndex = this.productsData.length - 1;
        this.$refs['product-item-' + productIndex][0].updateProductPrice(productID);

        Vue.nextTick(() => {
          this.productsData[productIndex].count = quantity;
        });
      });
    },
    prepareProductsData () {
      return this.productsData.map(product => {
        let productPriceWithDiscount = parseFloat(product.price);

        if (product.for_free) {
          productPriceWithDiscount = 0.01;
        }

        if (this.customerProductsDiscount > 0 && product.include_discounts && !product.use_custom_price && !product.for_free) {
          productPriceWithDiscount -= (productPriceWithDiscount * (this.customerProductsDiscount / 100));
        }

        let preparedGlobalServices = [];

        if (product.global_services) {
          let globalServicesIDs = Object.keys(product.global_services);

          for (let i = 0; i < globalServicesIDs.length; i++) {
            let foundedService = this.servicesData.filter(service => service.sid === parseInt(globalServicesIDs[i], 10));

            if (foundedService[0]) {
              preparedGlobalServices.push({
                new_global_service_id: globalServicesIDs[i],
                quantity: product.global_services[globalServicesIDs[i]]
              });
            } else {
              preparedGlobalServices.push({
                order_service_id: globalServicesIDs[i],
                quantity: product.global_services[globalServicesIDs[i]]
              });
            }
          }
        }

        return {
          id: product.product_id_in_order,
          product_id: product.id,
          containers: product.containers,
          destroyed: product.destroyed,
          purchase_quantity: product.purchase_quantity,
          purchase_product_id: product.purchase_product_id,
          price: CustomDecimals.roundProperly(productPriceWithDiscount),
          price_before_discount: CustomDecimals.roundProperly(product.price),
          quantity: product.count,
          description: product.description,
          description_for_customer: product.description_for_customer,
          file: product.file,
          is_sale: product.is_sale,
          exclude_in_invoice: +product.exclude_in_invoice,
          skip_moq: !product.moq,
          for_free: +product.for_free,
          use_custom_price: +product.use_custom_price,
          services: product.services.map(service => {
            let servicePriceWithDiscount = parseFloat(service.price);
            let setupPriceWithDiscount = parseFloat(service.setupPrice);

            if (this.customerServicesDiscount > 0 && !service.use_custom_price) {
              servicePriceWithDiscount -= (servicePriceWithDiscount * (this.customerServicesDiscount / 100));

              if (setupPriceWithDiscount > 0) {
                setupPriceWithDiscount -= (setupPriceWithDiscount * (this.customerServicesDiscount / 100));
              }
            }

            let serviceDataToSave = {
              id: service.service_id_in_order,
              service_id: service.id,
              contractor_id: service.contractor_id,
              price_multiple_from: service.serviceData.price_multiple_from,
              description: service.description,
              description_for_customer: service.description_for_customer,
              price: this.isIndent && this.isOffer ? 0 : CustomDecimals.roundProperly(servicePriceWithDiscount),
              price_before_discount: this.isIndent && this.isOffer ? 0 : CustomDecimals.roundProperly(service.price),
              price_setup: this.isIndent && this.isOffer ? 0 : CustomDecimals.roundProperly(setupPriceWithDiscount),
              price_setup_before_discount: this.isIndent && this.isOffer ? 0 : CustomDecimals.roundProperly(service.setupPrice),
              quantity: service.count,
              draft_required: service.draft_required,
              draft_at: service.draft_at,
              embroidery_type_id: service.embroidery_type_id ? service.embroidery_type_id : 0,
              embroidery_position_id: service.embroidery_position_id ? service.embroidery_position_id : 0,
              embroidery_width: service.embroidery_width,
              embroidery_height: service.embroidery_height,
              embroidery_color: service.embroidery_color,
              exclude_in_invoice: +service.exclude_in_invoice,
              use_custom_price: +service.use_custom_price,
              embroidery_estimated_date: service.embroidery_estimated_date,
              embroidery_date_tokens: service.embroidery_date_tokens,
              service_package_id: service.service_package_id
            };

            if (this.isIndent) {
              serviceDataToSave.samples = service.samples.map(sampleData => ({
                id: sampleData.id,
                status: sampleData.status,
                reject_reason: sampleData.reject_reason
              }));
            }

            return serviceDataToSave;
          }),
          global_services: preparedGlobalServices,
          services_packages: product.services_packages ? product.services_packages.map(pkg => ({
            id: pkg.id,
            group_id: pkg.group_id,
            quantity: pkg.quantity
          })) : []
        }
      });
    },
    prepareServicesData () {
      return this.servicesData.map(service => {
        let servicePriceWithDiscount = parseFloat(service.price);
        let setupPriceWithDiscount = parseFloat(service.setupPrice);

        if (this.customerServicesDiscount > 0 && !service.use_custom_price) {
          servicePriceWithDiscount -= (servicePriceWithDiscount * (this.customerServicesDiscount / 100));

          if (setupPriceWithDiscount > 0) {
            setupPriceWithDiscount -= (setupPriceWithDiscount * (this.customerServicesDiscount / 100));
          }
        }

        let dataToStore = {
          id: service.service_id_in_order,
          service_id: service.id,
          service_package_id: service.service_package_id,
          contractor_id: service.contractor_id,
          price_multiple_from: service.serviceData.price_multiple_from,
          price: CustomDecimals.roundProperly(servicePriceWithDiscount),
          price_before_discount: CustomDecimals.roundProperly(service.price),
          price_setup: CustomDecimals.roundProperly(setupPriceWithDiscount),
          price_setup_before_discount: CustomDecimals.roundProperly(service.setupPrice),
          description: service.description,
          description_for_customer: service.description_for_customer,
          quantity: service.count, // this.productsData.reduce((prev, product) => prev + (product.include_in_services ? parseInt(product.count, 10) : 0), 0)
          draft_required: service.draft_required,
          draft_at: service.draft_at,
          embroidery_type_id: service.embroidery_type_id ? service.embroidery_type_id : 0,
          embroidery_position_id: service.embroidery_position_id ? service.embroidery_position_id : 0,
          embroidery_width: service.embroidery_width,
          embroidery_height: service.embroidery_height,
          embroidery_color: service.embroidery_color,
          exclude_in_invoice: +service.exclude_in_invoice,
          use_custom_price: +service.use_custom_price,
          embroidery_estimated_date: service.embroidery_estimated_date,
          embroidery_date_tokens: service.embroidery_date_tokens
        };

        if (this.isIndent) {
          dataToStore.samples = service.samples.map(sampleData => ({
            id: sampleData.id,
            status: sampleData.status,
            reject_reason: sampleData.reject_reason
          }));
        }

        if (service.sid) {
          dataToStore.new_global_service_id = service.sid;
        }

        return dataToStore;
      });
    },
    prepareServicesPackagesData () {
      return this.servicesPackages ? this.servicesPackages.map(pkg => ({
        id: pkg.id,
        group_id: pkg.group_id,
        quantity: pkg.quantity
      })) : []
    },
    setUpProducts (orderProducts) {
      for (let i = 0; i < orderProducts.length; i++) {
        let productServices = [];

        for (let j = 0; j < orderProducts[i].services.length; j++) {
          productServices.push({
            id: orderProducts[i].services[j].service_id,
            contractor_id: orderProducts[i].services[j].contractor_id,
            contractor_name: orderProducts[i].services[j].contractor_name,
            service_id_in_order: orderProducts[i].services[j].id,
            name: orderProducts[i].services[j].name,
            hasDescription: !!orderProducts[i].services[j].description,
            hasCustomerDescription: !!orderProducts[i].services[j].description_for_customer,
            description: orderProducts[i].services[j].description,
            description_for_customer: orderProducts[i].services[j].description_for_customer,
            count: orderProducts[i].services[j].quantity,
            price: Number(orderProducts[i].services[j].price_before_discount).toFixed(2),
            totalPrice: orderProducts[i].services[j].price * orderProducts[i].services[j].quantity,
            setupPrice: Number(orderProducts[i].services[j].price_setup_before_discount).toFixed(2),
            draft_required: !!orderProducts[i].services[j].draft_required,
            draft_at: orderProducts[i].services[j].draft_at ? new Date(orderProducts[i].services[j].draft_at) : null,
            obtaining_at: orderProducts[i].services[j].obtaining_at ? new Date(orderProducts[i].services[j].obtaining_at) : null,
            accepted_at: orderProducts[i].services[j].accepted_at ? new Date(orderProducts[i].services[j].accepted_at) : null,
            dropbox_catalog: orderProducts[i].services[j].dropbox_catalog,
            ended_production_at: orderProducts[i].services[j].ended_production_at ? new Date(orderProducts[i].services[j].ended_production_at) : null,
            samples: orderProducts[i].services[j].samples,
            sample_images_url: orderProducts[i].services[j].sample_images_url,
            embriodery_id: orderProducts[i].services[j].embriodery_id,
            sample_images: orderProducts[i].services[j].sample_images,
            sample_hpl_accepted_at: orderProducts[i].services[j].sample_hpl_accepted_at,
            sample_status: orderProducts[i].services[j].sample_status,
            embroidery_type_id: orderProducts[i].services[j].embroidery_type_id ? orderProducts[i].services[j].embroidery_type_id : null,
            embroidery_type_name: orderProducts[i].services[j].embroidery_type_name,
            embroidery_position_id: orderProducts[i].services[j].embroidery_position_id ? orderProducts[i].services[j].embroidery_position_id : null,
            embroidery_position_name: orderProducts[i].services[j].embroidery_position_name,
            embroidery_width: orderProducts[i].services[j].embroidery_width,
            embroidery_height: orderProducts[i].services[j].embroidery_height,
            embroidery_color: orderProducts[i].services[j].embroidery_color,
            files: orderProducts[i].services[j].files,
            dst_number: orderProducts[i].services[j].dst_number,
            machines: orderProducts[i].services[j].machines,
            stitch_number: orderProducts[i].services[j].stitch_number,
            strand_colors: orderProducts[i].services[j].strand_colors,
            exclude_in_invoice: !!orderProducts[i].services[j].exclude_in_invoice,
            use_custom_price: !!orderProducts[i].services[j].use_custom_price,
            embroidery_estimated_date: orderProducts[i].services[j].embroidery_estimated_date,
            embroidery_date_tokens: [],
            service_package_id: orderProducts[i].services[j].service_package_id,
            serviceData: {
              type_id: orderProducts[i].services[j].type_id,
              price_multiple_from: orderProducts[i].services[j].price_multiple_from,
              prices: this.getServicePricesByID(orderProducts[i].services[j].service_id),
              uses_embroidery: orderProducts[i].services[j].uses_embroidery,
              group_id: orderProducts[i].services[j].service ? orderProducts[i].services[j].service.group_id : null
            }
          });
        }

        let productContainers = orderProducts[i].containers;

        if (orderProducts[i].container_id) {
          productContainers = [{
            container_id: orderProducts[i].container_id,
            container_quantity: orderProducts[i].container_quantity
          }];
        }

        let preparedGlobalServices = {};

        if (orderProducts[i].global_services) {
          for (let a = 0; a < orderProducts[i].global_services.length; a++) {
            let id = orderProducts[i].global_services[a].order_service_id;
            let qty = orderProducts[i].global_services[a].quantity;
            preparedGlobalServices[id] = qty;
          }
        }

        this.productsData.push({
          id: orderProducts[i].product_id,
          availability: orderProducts[i].availability,
          product_id_in_order: orderProducts[i].id,
          box_products: orderProducts[i].box_products,
          containers: productContainers,
          count: orderProducts[i].quantity,
          count_original: orderProducts[i].quantity,
          description: orderProducts[i].description,
          description_for_customer: orderProducts[i].description_for_customer,
          destroyed: orderProducts[i].destroyed,
          hasDescription: !!orderProducts[i].description,
          hasDescriptionForCustomer: !!orderProducts[i].description_for_customer,
          hasDestroyedItems: !!orderProducts[i].destroyed,
          hasVisualisation: !!orderProducts[i].file,
          productName: orderProducts[i].name,
          productCode: orderProducts[i].code,
          price: Number(orderProducts[i].price_before_discount).toFixed(2),
          file: orderProducts[i].file,
          file_url: orderProducts[i].file_url,
          is_sale: orderProducts[i].is_sale,
          totalPrice: orderProducts[i].price_before_discout * orderProducts[i].quantity,
          include_discounts: !!orderProducts[i].include_discounts,
          include_in_services: !!orderProducts[i].include_in_services,
          services: productServices,
          quantity_collected: orderProducts[i].quantity_collected,
          purchase_quantity: orderProducts[i].purchase_quantity,
          purchase_product_id: orderProducts[i].purchase_product_id,
          purchase_product: orderProducts[i].purchase_product,
          global_services: preparedGlobalServices,
          embroidery_ended_production_at: orderProducts[i].embroidery_ended_production_at,
          softlab_reservation_quantity: orderProducts[i].softlab_reservation_quantity,
          exclude_in_invoice: !!orderProducts[i].exclude_in_invoice,
          moq: !orderProducts[i].skip_moq,
          for_free: !!orderProducts[i].for_free,
          use_custom_price: !!orderProducts[i].use_custom_price,
          productData: {
            prices: this.getProductPricesByID(orderProducts[i].product_id)
          },
          services_packages: orderProducts[i] ? orderProducts[i].services_packages : [],
          embriodery_id: orderProducts[i].embriodery_id,
        });
      }
    },
    setUpProductsPricesData () {
      for (let i = 0; i < this.productsData.length; i++) {
        if (!this.productsData[i].productData.prices || !this.productsData[i].productData.prices.length) {
          this.productsData[i].productData.prices = this.getProductPricesByID(this.productsData[i].id);
        }

        for (let j = 0; j < this.productsData[i].services.length; j++) {
          if (!this.productsData[i].services[j].serviceData.prices.length) {
            this.productsData[i].services[j].serviceData.prices = this.getServicePricesByID(this.productsData[i].services[j].id);
          }
        }
      }
    },
    setUpServices (orderServices, orderServicesPackages) {
      if (!orderServices) {
        return;
      }

      for (let i = 0; i < orderServices.length; i++) {
        this.servicesData.push({
          id: orderServices[i].service_id,
          contractor_id: orderServices[i].contractor_id,
          contractor_name: orderServices[i].contractor_name,
          service_id_in_order: orderServices[i].id,
          name: orderServices[i].name,
          hasDescription: !!orderServices[i].description,
          hasCustomerDescription: !!orderServices[i].description_for_customer,
          description: orderServices[i].description,
          description_for_customer: orderServices[i].description_for_customer,
          count: orderServices[i].quantity,
          serviceName: orderServices[i].name,
          serviceCode: orderServices[i].code,
          price: Number(orderServices[i].price_before_discount).toFixed(2),
          totalPrice: orderServices[i].price * orderServices[i].quantity,
          setupPrice: Number(orderServices[i].price_setup_before_discount).toFixed(2),
          draft_required: !!orderServices[i].draft_required,
          draft_at: orderServices[i].draft_at ? new Date(orderServices[i].draft_at) : null,
          obtaining_at: orderServices[i].obtaining_at ? new Date(orderServices[i].obtaining_at) : null,
          accepted_at: orderServices[i].accepted_at ? new Date(orderServices[i].accepted_at) : null,
          dropbox_catalog: orderServices[i].dropbox_catalog,
          ended_production_at: orderServices[i].ended_production_at ? new Date(orderServices[i].ended_production_at) : null,
          sample_images_url: orderServices[i].sample_images_url,
          samples: orderServices[i].samples,
          embriodery_id: orderServices[i].embriodery_id,
          dst_number: orderServices[i].dst_number,
          machines: orderServices[i].machines,
          stitch_number: orderServices[i].stitch_number,
          strand_colors: orderServices[i].strand_colors,
          sample_images: orderServices[i].sample_images,
          sample_hpl_accepted_at: orderServices[i].sample_hpl_accepted_at,
          sample_status: orderServices[i].sample_status,
          embroidery_type_id: orderServices[i].embroidery_type_id ? orderServices[i].embroidery_type_id : null,
          embroidery_type_name: orderServices[i].embroidery_type_name,
          embroidery_position_id: orderServices[i].embroidery_position_id ? orderServices[i].embroidery_position_id : null,
          embroidery_position_name: orderServices[i].embroidery_position_name,
          embroidery_width: orderServices[i].embroidery_width,
          embroidery_height: orderServices[i].embroidery_height,
          embroidery_color: orderServices[i].embroidery_color,
          files: orderServices[i].files,
          exclude_in_invoice: !!orderServices[i].exclude_in_invoice,
          use_custom_price: !!orderServices[i].use_custom_price,
          embroidery_estimated_date: orderServices[i].embroidery_estimated_date,
          embroidery_date_tokens: [],
          service_package_id: orderServices[i].service_package_id,
          serviceData: {
            type_id: orderServices[i].type_id,
            price_multiple_from: orderServices[i].price_multiple_from,
            prices: this.getServicePricesByID(orderServices[i].service_id),
            uses_embroidery: orderServices[i].uses_embroidery,
            group_id: orderServices[i].service ? orderServices[i].service.group_id : null
          }
        });

        Vue.set(this, 'servicesPackages', orderServicesPackages);
      }
    },
    setUpServicesPricesData () {
      for (let i = 0; i < this.servicesData.length; i++) {
        if (!this.servicesData[i].serviceData.prices.length) {
          this.servicesData[i].serviceData.prices = this.getServicePricesByID(this.servicesData[i].id);
        }
      }
    },
    getServicePricesByID (serviceID) {
      let foundedService = this.services.filter(service => service.id === serviceID);

      if (foundedService.length) {
        return foundedService[0].prices;
      }

      return [];
    },
    getProductPricesByID (productID) {
      let foundedProduct = this.products.filter(product => product.id === productID);

      if (foundedProduct.length) {
        return foundedProduct[0].prices;
      }

      return [];
    },
    refreshProductsAvailability () {
      let productIDs = this.productsData
        .map(productData => parseInt(productData.id, 10))
        .filter(id => id > 0);

      if (!productIDs.length && this.debouncedRefreshProductsAvailability) {
        this.debouncedRefreshProductsAvailability.cancel();
        this.availabilityRefreshInProgress = false;
        return;
      }

      this.availabilityRefreshInProgress = true;
      this.debouncedRefreshProductsAvailability(productIDs);
    },
    checkAvailability () {
      let productIDs = this.productsData.map(productData => productData.id);
      let productsAvailabilityTemp = this.products.filter(product => productIDs.indexOf(product.id) > -1);
      let productsAvailability = {};

      for (let i = 0; i < productsAvailabilityTemp.length; i++) {
        let productData = productsAvailabilityTemp[i];
        productsAvailability[productData.id] = productData.availability;
      }

      let dataToCheck = this.productsData.map(productData => ({
        product_id: productData.id,
        container_quantity: productData.container_quantity || 0,
        count: parseInt(productData.count, 10)
      }));

      let hasContainerProducts = false;
      let indentProductIDs = this.indentProducts.map(product => product.id);

      for (let i = 0; i < dataToCheck.length; i++) {
        if (indentProductIDs.indexOf(dataToCheck[i].product_id) > -1) {
          // eslint-disable-next-line
          continue;
        }

        if (typeof productsAvailability[dataToCheck[i].product_id] !== 'number') {
          this.$bus.$emit('order-creator-check-availability', 'no-availability-data');
          return;
        }

        if (dataToCheck[i].count - dataToCheck[i].container_quantity > productsAvailability[dataToCheck[i].product_id]) {
          this.$bus.$emit('order-creator-check-availability', 'no-stocks');
          return;
        }

        if (dataToCheck[i].container_quantity) {
          hasContainerProducts = true;
        }
      }

      if (hasContainerProducts) {
        this.$bus.$emit('order-creator-check-availability', 'has-container-products');
        return;
      }

      this.$bus.$emit('order-creator-check-availability', 'ok');
    },
    cartonsAmount () {
      let sum = 0;
      let foundProductWithoutPiecesInCartoonSet = false;

      for (let i = 0; i < this.productsData.length; i++) {
        let productID = this.productsData[i].id;
        let qty = this.productsData[i].count;
        let foundedProduct = this.products.filter(product => product.id === productID);

        if (foundedProduct[0]) {
          let piecesInCarton = parseInt(foundedProduct[0].pieces_in_carton, 10);

          if (piecesInCarton) {
            let amount = qty / piecesInCarton;
            sum += amount;
          } else {
            foundProductWithoutPiecesInCartoonSet = true;
          }
        }
      }

      return {
        amount: Math.ceil(sum),
        productsWithoutPieces: foundProductWithoutPiecesInCartoonSet
      };
    },
    removeAllProducts () {
      Vue.swal({
        title: this.$t('order.removeAllProductsConfirmTitle'),
        text: this.$t('order.removeAllProductsConfirmText'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yesRemove'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          Vue.set(this, 'productsData', []);
          Vue.set(this, 'servicesData', []);
        }
      });
    },
    roundProperly (value, decimals = 2) {
      return CustomDecimals.roundProperly(value, decimals);
    },
    openProductsSearchPopup (productIndex) {
      this.productsSearchPopupMultiMode = false;
      this.productsSearchPopupIsOpen = true;
      this.productsSearchPopupProductIndex = productIndex;
    },
    openMultiProductsSearchPopup () {
      this.productsSearchPopupMultiMode = true;
      this.productsSearchPopupIsOpen = true;
    },
    closeProductsSearchPopup () {
      this.productsSearchPopupIsOpen = false;
      this.productsSearchPopupMultiMode = false;
    },
    setProduct (productID, productIndex, productQuantity) {
      Vue.set(this.productsData[productIndex], 'id', productID);
      this.$bus.$emit('order-change-product-from-popup', productIndex, productID);

      if (productQuantity !== 'skip') {
        if (productQuantity) {
          Vue.set(this.productsData[productIndex], 'count', productQuantity);
        } else {
          Vue.set(this.productsData[productIndex], 'count', 1);
        }
      }
    },
    setIndentProduct (productID, productIndex) {
      this.$bus.$emit('order-change-indent-product-from-popup', productIndex, productID);
    },
    canBeVirtualOrder () {
      for (let i = 0; i < this.productsData.length; i++) {
        if (this.productsData[i].purchase_product_id || this.productsData[i].purchase_id) {
          return false;
        }

        if (this.productsData[i].containers.length || this.productsData[i].container_id) {
          return false;
        }
      }

      return true;
    },
    updatePricesUsingCurrentPricing () {
      Vue.swal({
        title: this.$t('order.doYouWantToUpdatePrices'),
        text: this.$t('order.doYouWantToUpdatePricesDesc'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yesUpdate'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.priceUpdateInProgress = true;
          this.getPricesFromCurrentPricing();
        }
      });
    },
    getPricesFromCurrentPricing () {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/get-current-prices/' + parseInt(this.$route.params.id, 10),
        method: 'get',
        formData: {},
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.errorOccuredDesc'),
        successAction: (response) => {
          this.priceUpdateInProgress = false;

          if (response && response.prices) {
            this.replacePricesUsingCurrentPricing(response.prices);
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.errorOccuredDesc'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.priceUpdateInProgress = false;
        }
      });
    },
    replacePricesUsingCurrentPricing (prices) {
      let productPrices = {};
      let productServicePrices = {};
      let globalServicePrices = {};

      for (let i = 0; i < prices.products.length; i++) {
        let product = prices.products[i];
        productPrices[product.id] = {
          name: product.name,
          newPrice: product.current_price,
          oldPrice: parseFloat(product.price_before_discount),
          applied: false
        };

        for (let j = 0; j < product.services.length; j++) {
          let service = prices.products[i].services[j];
          productServicePrices[service.id] = {
            name: service.name,
            newPrice: service.current_price,
            oldPrice: parseFloat(service.price_before_discount),
            newSetupPrice: this.includeSetupPrice ? service.current_setup_price : 0,
            oldSetupPrice: this.includeSetupPrice ? parseFloat(service.price_setup_before_discount) : 0,
            newMultiplier: service.current_price_multiple_from,
            oldMultiplier: service.price_multiple_from,
            applied: false
          };
        }
      }

      for (let i = 0; i < prices.services.length; i++) {
        let service = prices.services[i];
        globalServicePrices[service.id] = {
          name: service.name,
          newPrice: service.current_price,
          oldPrice: parseFloat(service.price_before_discount),
          newSetupPrice: this.includeSetupPrice ? service.current_setup_price : 0,
          oldSetupPrice: this.includeSetupPrice ? parseFloat(service.price_setup_before_discount) : 0,
          newMultiplier: service.current_price_multiple_from,
          oldMultiplier: service.price_multiple_from,
          applied: false
        };
      }

      for (let i = 0; i < this.productsData.length; i++) {
        let product = this.productsData[i];
        let productID = product.product_id_in_order;

        if (this.productsData[i].for_free) {
          productPrices[productID].for_free = true;
        }

        if (this.productsData[i].use_custom_price) {
          productPrices[productID].use_custom_price = true;
        }

        if (typeof productPrices[productID] !== 'undefined' && !this.productsData[i].for_free && !this.productsData[i].use_custom_price) {
          Vue.set(this.productsData[i], 'price', productPrices[productID].newPrice);
          productPrices[productID].applied = true;
        }

        for (let j = 0; j < this.productsData[i].services.length; j++) {
          let service = this.productsData[i].services[j];
          let serviceID = service.service_id_in_order;

          if (service.use_custom_price) {
            productServicePrices[serviceID].use_custom_price = true;
          }

          if (typeof productServicePrices[serviceID] !== 'undefined' && !service.use_custom_price) {
            Vue.set(this.productsData[i].services[j].serviceData, 'price_multiple_from', productServicePrices[serviceID].newMultiplier);
            Vue.set(this.productsData[i].services[j], 'price', productServicePrices[serviceID].newPrice);
            Vue.set(this.productsData[i].services[j], 'setupPrice', productServicePrices[serviceID].newSetupPrice);
            productServicePrices[serviceID].applied = true;
          }
        }
      }

      for (let i = 0; i < this.servicesData.length; i++) {
        let service = this.servicesData[i];
        let serviceID = service.service_id_in_order;

        if (service.use_custom_price) {
          globalServicePrices[serviceID].use_custom_price = true;
        }

        if (typeof globalServicePrices[serviceID] !== 'undefined' && !service.use_custom_price) {
          Vue.set(this.servicesData[i].serviceData, 'price_multiple_from', globalServicePrices[serviceID].newMultiplier);
          Vue.set(this.servicesData[i], 'price', globalServicePrices[serviceID].newPrice);
          Vue.set(this.servicesData[i], 'setupPrice', globalServicePrices[serviceID].newSetupPrice);
          globalServicePrices[serviceID].applied = true;
        }
      }

      this.$bus.$emit('show-price-changes-popup', productPrices, productServicePrices, globalServicePrices);
    },
    showSamplePreviewPopup (url) {
      this.samplePreviewLoaded = false;
      this.samplePreviewURL = url;

      Vue.nextTick(() => {
        this.displaySamplePreviewPopup = true;
      });
    },
    hideSamplePreviewPopup () {
      this.displaySamplePreviewPopup = false;
      this.samplePreviewURL = '';
      this.samplePreviewLoaded = false;
    },
    openInNewTab (url) {
      window.open(url, '_blank', 'noreferrer');
    },
    showBoxProductsCommentsPopup (dataToDisplay) {
      this.boxProductsComments = dataToDisplay;
      this.displayBoxProductsCommentsPopup = true;
    },
    closeBoxProductsCommentsPopup () {
      this.boxProductsComments = null;
      this.displayBoxProductsCommentsPopup = false;
    },
    resetCustomPrices () {
      if (this.orderSetUpInProgress) {
        return;
      }

      let customPriceFounded = false;

      for (let i = 0; i < this.productsData.length; i++) {
        let services = this.productsData[i].services;

        if (this.productsData[i].use_custom_price) {
          this.productsData[i].use_custom_price = false;
          customPriceFounded = true;
        }

        for (let j = 0; j < services.length; j++) {
          if (services[j].use_custom_price) {
            services[j].use_custom_price = false;
            customPriceFounded = true;
          }
        }
      }

      for (let i = 0; i < this.servicesData.length; i++) {
        if (this.servicesData[i].use_custom_price) {
          this.servicesData[i].use_custom_price = false;
          customPriceFounded = true;
        }
      }

      if (customPriceFounded) {
        Vue.swal({
          title: this.$t('order.customPricesResetTitle'),
          html: this.$t('order.customPricesResetDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
      }
    },
    removeServicePackageFromProduct (operationData) {
      let productIndex = operationData.productIndex;
      let pkgID = operationData.packageID;
      let product = this.productsData[productIndex];

      product.services_packages = product.services_packages.filter(pkg => pkg.id !== pkgID);
      product.services = product.services.filter(service => service.service_package_id !== pkgID);
    },
    updateServicePackageQtyInProduct (operationData) {
      let productIndex = operationData.productIndex;
      let pkgID = operationData.packageID;
      let product = this.productsData[productIndex];
      let packageToUpdate = product.services_packages.filter(pkg => pkg.id === pkgID);

      if (packageToUpdate && packageToUpdate[0]) {
        packageToUpdate[0].quantity = operationData.qty;
      }
    },
    updateGlobalServicePackageQty (operationData) {
      let pkgID = operationData.packageID;
      let packageToUpdate = this.servicesPackages.filter(pkg => pkg.id === pkgID);

      if (packageToUpdate && packageToUpdate[0]) {
        packageToUpdate[0].quantity = operationData.qty;
      }
    },
    removeGlobalServicePackage (pkgID) {
      let removedServices = this.servicesData.filter(service => service.service_package_id === pkgID);

      this.servicesPackages = this.servicesPackages.filter(pkg => pkg.id !== pkgID);
      this.servicesData = this.servicesData.filter(service => service.service_package_id !== pkgID);
      this.expandedServicePackages = this.expandedServicePackages.filter(packageID => packageID !== pkgID);

      for (let i = 0; i < this.productsData.length; i++) {
        let assignments = this.productsData[i].global_services;

        if (!assignments) {
          // eslint-disable-next-line
          continue;
        }

        for (let j = 0; j < removedServices.length; j++) {
          let serviceKeys = [removedServices[j].service_id_in_order, removedServices[j].sid];

          for (let k = 0; k < serviceKeys.length; k++) {
            if (serviceKeys[k] && assignments[serviceKeys[k]] !== undefined) {
              Vue.delete(assignments, serviceKeys[k]);
            }
          }
        }
      }
    },
    showServicesPackagePopup (productIndex = null) {
      if (!this.allowServicePackagesAdd || this.readOnly) {
        return;
      }

      this.displayServicesPackagePopup = true;
      this.selectedServicesPackage = null;
      this.assignServicesPackageTo = productIndex;
    },
    hideServicesPackagePopup () {
      this.displayServicesPackagePopup = false;
      this.selectedServicesPackage = null;
      this.assignServicesPackageTo = null;
    },
    filterByForServicesPackages (option, label, search) {
      return (option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    validateServicesPackageForm () {
      FormUtils.validate(this.$refs['select-services-package-form'], this.addServicesPackage);
    },
    buildEmptyPackageServiceData (packageID, count) {
      return {
        sid: null,
        id: '',
        serviceData: {},
        price: '',
        count,
        setupPrice: '',
        totalPrice: '',
        totalDiscountedPrice: '',
        exclude_in_invoice: false,
        use_custom_price: false,
        samples: [],
        embroidery_estimated_date: null,
        embroidery_date_tokens: [],
        embroidery_type_id: null,
        embroidery_type_name: null,
        embroidery_position_id: null,
        embroidery_position_name: null,
        embroidery_width: null,
        embroidery_height: null,
        embroidery_color: null,
        service_package_id: packageID
      };
    },
    addServicesPackage () {
      if (!this.selectedServicesPackage) {
        return;
      }

      let selectedGroup = this.packageGroups.filter(group => group.id === this.selectedServicesPackage);

      if (!selectedGroup || !selectedGroup[0]) {
        return;
      }

      let group = selectedGroup[0];
      let packageID = 'new-package-' + (+new Date());
      let servicesToAssign = group.package_services ? group.package_services.slice() : [];

      if (group.has_stitch_calculator) {
        // Additional empty slot for the service selected via the calculator
        servicesToAssign.push(null);
      }

      let productIndex = this.assignServicesPackageTo;
      let baseSid = +new Date();

      if (productIndex !== null) {
        let product = this.productsData[productIndex];

        if (!product) {
          return;
        }

        if (!product.services_packages) {
          Vue.set(product, 'services_packages', []);
        }

        product.services_packages.push({
          id: packageID,
          group_id: group.id,
          group_name: group.name,
          quantity: product.count
        });

        let rowStartIndex = product.services.length;

        for (let i = 0; i < servicesToAssign.length; i++) {
          let serviceData = this.buildEmptyPackageServiceData(packageID, product.count);
          serviceData.sid = baseSid + i + 1;
          product.services.push(serviceData);
        }

        this.expandedServicePackages.push(packageID);

        Vue.nextTick(() => {
          let productRef = this.$refs['product-item-' + productIndex] ? this.$refs['product-item-' + productIndex][0] : null;

          for (let i = 0; i < servicesToAssign.length; i++) {
            if (!servicesToAssign[i]) {
              // eslint-disable-next-line
              continue;
            }

            let rowIndex = rowStartIndex + i;
            product.services[rowIndex].id = servicesToAssign[i].id;
            Vue.set(product.services[rowIndex], 'name', servicesToAssign[i].name);

            if (productRef && productRef.$refs['product-' + productIndex + '-service-' + rowIndex]) {
              productRef.$refs['product-' + productIndex + '-service-' + rowIndex][0].updateServicePrices(servicesToAssign[i].id);
            }
          }
        });
      } else {
        if (!this.servicesPackages) {
          Vue.set(this, 'servicesPackages', []);
        }

        this.servicesPackages.push({
          id: packageID,
          group_id: group.id,
          group_name: group.name,
          quantity: this.totalProductItemsCountWithExcluded
        });

        let rowStartIndex = this.servicesData.length;

        for (let i = 0; i < servicesToAssign.length; i++) {
          let serviceData = this.buildEmptyPackageServiceData(packageID, this.totalProductItemsCountWithExcluded);
          serviceData.sid = baseSid + i + 1;
          this.servicesData.push(serviceData);

          for (let j = 0; j < this.productsData.length; j++) {
            Vue.set(this.productsData[j].global_services, serviceData.sid, this.productsData[j].count);
          }
        }

        this.expandedServicePackages.push(packageID);

        Vue.nextTick(() => {
          for (let i = 0; i < servicesToAssign.length; i++) {
            if (!servicesToAssign[i]) {
              // eslint-disable-next-line
              continue;
            }

            let rowIndex = rowStartIndex + i;
            this.servicesData[rowIndex].id = servicesToAssign[i].id;
            Vue.set(this.servicesData[rowIndex], 'name', servicesToAssign[i].name);

            let rowRef = this.$refs['global-service-in-package-' + rowIndex];

            if (rowRef && rowRef[0]) {
              rowRef[0].updateServicePrices(servicesToAssign[i].id);
            }
          }
        });
      }

      Vue.nextTick(() => {
        this.hideServicesPackagePopup();
      });
    },
    applyCalculatorResult (payload) {
      let servicesList;
      let isProductPackage = payload.productIndex !== null && payload.productIndex !== undefined;

      if (isProductPackage) {
        let product = this.productsData[payload.productIndex];

        if (!product) {
          return;
        }

        servicesList = product.services;
      } else {
        servicesList = this.servicesData;
      }

      let calculatorIDs = this.calculatedServicesList[payload.packageID] || [];
      let slotIndex = -1;

      for (let i = 0; i < servicesList.length; i++) {
        let service = servicesList[i];

        if (service.service_package_id !== payload.packageID) {
          // eslint-disable-next-line
          continue;
        }

        if (!service.id || calculatorIDs.indexOf(service.id) > -1) {
          slotIndex = i;
        }
      }

      if (slotIndex === -1) {
        return;
      }

      let slotService = servicesList[slotIndex];

      if (!payload.serviceID) {
        slotService.id = '';
        Vue.set(slotService, 'name', null);
        Vue.set(slotService, 'serviceData', {});
        slotService.price = '';
        slotService.setupPrice = '';
        slotService.totalPrice = '';
        Vue.set(slotService, 'totalDiscountedPrice', '');
        Vue.set(slotService, 'embroidery_type_id', null);
        Vue.set(slotService, 'embroidery_type_name', null);
        return;
      }

      slotService.id = payload.serviceID;
      Vue.set(slotService, 'name', payload.serviceName);

      if (payload.embroideryTypeID) {
        Vue.set(slotService, 'embroidery_type_id', payload.embroideryTypeID);
        Vue.set(slotService, 'embroidery_type_name', payload.embroideryTypeName);
      }

      Vue.nextTick(() => {
        if (isProductPackage) {
          let productRef = this.$refs['product-item-' + payload.productIndex] ? this.$refs['product-item-' + payload.productIndex][0] : null;

          if (productRef && productRef.$refs['product-' + payload.productIndex + '-service-' + slotIndex]) {
            productRef.$refs['product-' + payload.productIndex + '-service-' + slotIndex][0].updateServicePrices(payload.serviceID);
          }
        } else {
          let rowRef = this.$refs['global-service-in-package-' + slotIndex];

          if (rowRef && rowRef[0]) {
            rowRef[0].updateServicePrices(payload.serviceID);
          }
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('products-search-popup-select-product', this.setProduct);
    this.$bus.$off('products-search-popup-select-multiple-products', this.setMultipleProducts);
    this.$bus.$off('set-new-indent-product-in-order', this.setIndentProduct);
    this.$bus.$off('order-creator-move-product-from-inquiry', this.moveProductFromInquiry);
    this.$bus.$off('order-status-check-availability', this.checkAvailability);
    this.$bus.$off('products-search-popup-open', this.openProductsSearchPopup);
    this.$bus.$off('products-search-popup-close', this.closeProductsSearchPopup);
    this.$bus.$off('order-show-sample-preview', this.showSamplePreviewPopup);
    this.$bus.$off('order-creator-box-products-comments-popup-open', this.showBoxProductsCommentsPopup);
    this.$bus.$off('order-creator-box-products-comments-popup-close', this.closeBoxProductsCommentsPopup);
    this.$bus.$off('order-reset-custom-prices', this.resetCustomPrices);
    this.$bus.$off('order-remove-service-package-from-product', this.removeServicePackageFromProduct);
    this.$bus.$off('order-update-service-package-qty-in-product', this.updateServicePackageQtyInProduct);
    this.$bus.$off('order-update-global-service-package-qty', this.updateGlobalServicePackageQty);
    this.$bus.$off('order-remove-global-service-package', this.removeGlobalServicePackage);
    this.$bus.$off('order-creator-add-services-package', this.showServicesPackagePopup);
    this.$bus.$off('order-package-calculator-result', this.applyCalculatorResult);
  }
};
</script>

<style lang="scss" scoped>
.is-v-select-wrapper > div {
  width: 100%;
}
</style>
